import { Icon } from '@iconify/react';
import React, { useEffect, useRef, useState } from 'react';
import { NotificationTarget } from '../../types/ApiTypes';
import { useApi } from '../hooks/useApi';

type Props = {
  open: boolean;
  onClose: () => void;
  onSend?: (payload: NotificationPayload) => void;
}

import { RecurrenceType } from '../../types/ApiTypes';

export type NotificationPayload = {
  title: string;
  body: string;
  targetType: NotificationTarget;
  userId?: string;
  scheduledAt?: string;
  isRecurring?: boolean;
  recurrenceType?: RecurrenceType;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay?: string;
}

type UserItem = { id: string; email: string; username?: string | null }

const inputStyle = { boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.02)' }

export default function SendPushModal({ open, onClose, onSend }: Props): React.JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [targetType, setTargetType] = useState<NotificationTarget>(NotificationTarget.ALL_USERS)
  const [selectedUser, setSelectedUser] = useState<UserItem | null>(null)
  const [isScheduled, setIsScheduled] = useState(false)
  const [scheduledDate, setScheduledDate] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(RecurrenceType.DAILY)
  const [dayOfWeek, setDayOfWeek] = useState(1)
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [timeOfDay, setTimeOfDay] = useState('09:00')
  const [openUsers, setOpenUsers] = useState(false)
  const usersBtnRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!open) return
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open, onClose])

  if (!open) return null

  function handleSend() {
    const payload: NotificationPayload = {
      title,
      body,
      targetType
    }

    if (targetType === NotificationTarget.USER && selectedUser) {
      payload.userId = selectedUser.id
    }

    if (isScheduled && scheduledDate && scheduledTime) {
      payload.scheduledAt = `${scheduledDate}T${scheduledTime}:00Z`
    }

    if (isRecurring) {
      payload.isRecurring = true
      payload.recurrenceType = recurrenceType
      payload.timeOfDay = timeOfDay
      if (recurrenceType === 'weekly') payload.dayOfWeek = dayOfWeek
      if (recurrenceType === 'monthly') payload.dayOfMonth = dayOfMonth
    }

    onSend?.(payload)
    resetForm()
    onClose()
  }

  function resetForm() {
    setTitle('')
    setBody('')
    setTargetType(NotificationTarget.ALL_USERS)
    setSelectedUser(null)
    setIsScheduled(false)
    setScheduledDate('')
    setScheduledTime('')
    setIsRecurring(false)
    setRecurrenceType(RecurrenceType.DAILY)
    setDayOfWeek(1)
    setDayOfMonth(1)
    setTimeOfDay('09:00')
  }

  const canSend = title && body && (targetType !== NotificationTarget.USER || selectedUser)

  return (
    <div role="dialog" aria-label="Send push notification" className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(16,24,40,0.4)] p-6">
      <div ref={ref} className="w-[450px] bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(16,24,40,0.12)] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Send push notification</h3>
          <button onClick={onClose} className="text-[#1D1D1F]"><Icon icon="material-symbols:close" width={16} /></button>
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#56565C]">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="System Maintenance" className="font-normal w-full mt-1 p-2 rounded-md border border-[#EDEDEE] text-sm placeholder:text-[#CACACC]" style={inputStyle} />
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#56565C]">Body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Write notification message..." className="font-normal w-full mt-1 p-2 rounded-md border border-[#EDEDEE] placeholder:text-[#CACACC] text-sm h-28 resize-none" style={inputStyle} />
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#56565C]">Target</label>
          <select value={targetType} onChange={e => { setTargetType(e.target.value as NotificationTarget); setSelectedUser(null) }} className="w-full mt-1 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle}>
            <option value={NotificationTarget.ALL_USERS}>All Users</option>
            <option value={NotificationTarget.USER}>Specific User</option>
            <option value={NotificationTarget.ADMINS}>Admins</option>
          </select>
        </div>

        {targetType === NotificationTarget.USER && (
          <div className="mt-4">
            <label className="text-sm text-[#56565C]">Select User</label>
            <div className='relative mt-1'>
              <button
                ref={usersBtnRef}
                onClick={() => setOpenUsers(o => !o)}
                className='w-full h-9 px-2 rounded-md border border-[#E7E7E9] bg-white flex items-center justify-between text-sm hover:bg-[#F9F9FA]'
              >
                <span className={selectedUser ? 'text-[#101018]' : 'text-[#9C9C9F]'}>
                  {selectedUser ? selectedUser.email : 'Select user'}
                </span>
                <Icon icon='solar:alt-arrow-down-linear' width={16} className='text-[#B3B3B6]' />
              </button>

              {openUsers && (
                <div className='absolute z-40 top-[42px] left-0 right-0'>
                  <UserSearchDropdown
                    open={openUsers}
                    onClose={() => setOpenUsers(false)}
                    anchorRef={usersBtnRef}
                    onSelect={(user) => { setSelectedUser(user); setOpenUsers(false) }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <input type="checkbox" checked={isScheduled} onChange={e => setIsScheduled(e.target.checked)} className="accent-[#AF7AD5]" />
          <label className="text-sm text-[#56565C]">Schedule for later</label>
        </div>

        {isScheduled && (
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-[#56565C]">Date</label>
              <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="w-full mt-2 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle} />
            </div>
            <div>
              <label className="text-sm text-[#56565C]">Time</label>
              <input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} className="w-full mt-2 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle} />
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} className="accent-[#AF7AD5]" />
          <label className="text-sm text-[#56565C]">Recurring notification</label>
        </div>

        {isRecurring && (
          <>
            <div className="mt-4">
              <label className="text-sm text-[#56565C]">Recurrence Type</label>
              <select value={recurrenceType} onChange={e => setRecurrenceType(e.target.value as RecurrenceType)} className="w-full mt-2 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle}>
                <option value={RecurrenceType.DAILY}>Daily</option>
                <option value={RecurrenceType.WEEKLY}>Weekly</option>
                <option value={RecurrenceType.MONTHLY}>Monthly</option>
              </select>
            </div>

            {recurrenceType === RecurrenceType.WEEKLY && (
              <div className="mt-4">
                <label className="text-sm text-[#56565C]">Day of Week</label>
                <select value={dayOfWeek} onChange={e => setDayOfWeek(Number(e.target.value))} className="w-full mt-2 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle}>
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}

            {recurrenceType === RecurrenceType.MONTHLY && (
              <div className="mt-4">
                <label className="text-sm text-[#56565C]">Day of Month</label>
                <input type="number" min="1" max="31" value={dayOfMonth} onChange={e => setDayOfMonth(Number(e.target.value))} className="w-full mt-2 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle} />
              </div>
            )}

            <div className="mt-4">
              <label className="text-sm text-[#56565C]">Time of Day</label>
              <input type="time" value={timeOfDay} onChange={e => setTimeOfDay(e.target.value)} className="w-full mt-2 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle} />
            </div>
          </>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 rounded-full text-sm bg-[#F7F7F8] hover:bg-[#E3E3E5] transition cursor-pointer">Cancel</button>
          <button onClick={handleSend} disabled={!canSend} className="px-6 py-2 rounded-full bg-[#AF7AD5] text-white text-sm hover:bg-[#9B6BBF] transition cursor-pointer disabled:bg-[#E3D4EF] disabled:cursor-not-allowed">Send</button>
        </div>
      </div>
    </div>
  )
}

function UserSearchDropdown({ open, onClose, anchorRef, onSelect }: {
  open: boolean
  onClose: () => void
  anchorRef: React.RefObject<HTMLButtonElement | null>
  onSelect: (user: UserItem) => void
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<UserItem[]>([])
  const [loading, setLoading] = useState(false)
  const { user: userService } = useApi()

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (!open) return
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node) && anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', handle)
    return () => window.removeEventListener('mousedown', handle)
  }, [open, onClose, anchorRef])

  useEffect(() => {
    const searchUsers = async () => {
      setLoading(true)
      const res = await userService.searchUsers({ search: query })
      if (res.status === 'success' && res.data) {
        setUsers(res.data.map(u => ({ id: u.id, email: u.email, ...(u.username !== undefined && { username: u.username }) })))
      }
      setLoading(false)
    }
    if (open) searchUsers()
  }, [query, open, userService])

  return (
    <div ref={wrapRef} className='bg-white border border-[#EDEDEE] rounded-lg shadow-[0_4px_6px_-2px_rgba(16,24,40,0.04),0_14px_16px_-2px_rgba(16,24,40,0.08)] p-2'>
      <div className='px-2 py-2'>
        <div className='flex items-center gap-2 px-2 py-2 border border-[#EDEDEE] rounded-md'>
          <Icon icon='solar:magnifer-linear' width={14} className='text-[#CACACC]' />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder='Search by username or email' className='flex-1 text-sm outline-none' />
        </div>
      </div>

      <div className='max-h-56 overflow-auto'>
        {loading ? (
          <div className='px-3 py-6 flex justify-center'>
            <Icon icon='svg-spinners:ring-resize' width={24} className='text-[#AF7AD5]' />
          </div>
        ) : (
          <>
            {users.map(u => (
              <button key={u.id} onClick={() => onSelect(u)} className='w-full flex items-center gap-3 px-2 py-2 hover:bg-[#F9F9FA] cursor-pointer text-sm text-left'>
                <span className='truncate'>{u.email}</span>
                <span className='ml-auto px-2 py-0.5 rounded-full bg-[#F1F1F3] text-[#6D6D72] text-[11px]'>@{u.username ?? 'N/A'}</span>
              </button>
            ))}
            {users.length === 0 && !loading && (
              <div className='px-3 py-6 text-xs text-[#9C9C9F] text-center'>No users found.</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
